/**
 * ProofConfirmationPanel — §23 Step 14.
 *
 * Instructor-side surface for promoting `ai_assisted` or
 * `instructor_suggested` evidence rows to `instructor_confirmed`
 * or `instructor_confirmed_partial`.
 *
 * Doctrine:
 *   • Backend owns ALL wording (badge labels, provenance lines,
 *     reasons). The frontend never invents text.
 *   • One bell receipt fires on the learner's side per meaningful
 *     promotion — handled server-side. We just call POST and
 *     re-fetch.
 *   • No bulk-confirm UI at Step 14. Each promotion is an explicit
 *     instructor action with two clearly-labelled buttons:
 *       "Confirm fully"           → instructor_confirmed
 *       "Confirm partially"       → instructor_confirmed_partial
 *
 * Mounted by callers like:
 *     <ProofConfirmationPanel pupilUserId={uid} />
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const _STATE_COPY = {
  instructor_confirmed:           "Confirmed",
  instructor_confirmed_partial:   "Partly confirmed",
  instructor_suggested:           "Suggested",
  ai_assisted:                    "AI-suggested",
};

function StateChip({ state }) {
  const label = _STATE_COPY[state] || state;
  const aiTone = state === "ai_assisted";
  const partialTone = state === "instructor_confirmed_partial";
  const confirmedTone = state === "instructor_confirmed";
  const cls = [
    "inline-block text-[10px] uppercase tracking-[0.12em] font-bold px-2 py-0.5 rounded-md",
    confirmedTone && "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
    partialTone   && "bg-sky-50 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300",
    !confirmedTone && !partialTone && !aiTone && "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300",
    aiTone        && "bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
  ].filter(Boolean).join(" ");
  return <span className={cls} data-testid={`state-chip-${state}`}>{label}</span>;
}

export default function ProofConfirmationPanel({
  pupilUserId,
  hideWhenEmpty = false,
  heading = null,
}) {
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const fetchRows = useCallback(async () => {
    if (!pupilUserId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(
        `${API}/api/instructor/pupils/${encodeURIComponent(pupilUserId)}/evidence`,
        { withCredentials: true },
      );
      setSkills(res.data?.skills || []);
    } catch (err) {
      setError(
        err?.response?.status === 403
          ? "You're not linked to this pupil."
          : "Couldn't load evidence right now.",
      );
    } finally {
      setLoading(false);
    }
  }, [pupilUserId]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const promote = useCallback(async (evidenceId, newState) => {
    setBusyId(evidenceId);
    try {
      await axios.post(
        `${API}/api/instructor/evidence/${evidenceId}/confirm`,
        { new_state: newState },
        { withCredentials: true },
      );
      await fetchRows();
    } catch (err) {
      // Backend returns 409 on illegal transitions with a calm
      // human message in detail.message. Show that verbatim.
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "object"
        ? (detail.message || "Could not confirm.")
        : (detail || "Could not confirm.");
      setError(msg);
    } finally {
      setBusyId(null);
    }
  }, [fetchRows]);

  if (loading) {
    if (hideWhenEmpty) return null;
    return (
      <section data-testid="proof-confirmation-panel-loading" className="text-sm text-slate-500">
        Loading evidence…
      </section>
    );
  }
  if (error) {
    return (
      <section data-testid="proof-confirmation-panel-error" className="text-sm text-rose-600 dark:text-rose-400">
        {error}
      </section>
    );
  }
  if (!skills.length) {
    if (hideWhenEmpty) return null;
    return (
      <section data-testid="proof-confirmation-panel-empty" className="text-sm text-slate-500 dark:text-slate-400">
        No evidence yet for this pupil. Once a lesson is released or a review completes, evidence will appear here.
      </section>
    );
  }

  return (
    <section data-testid="proof-confirmation-panel" className="space-y-4">
      {heading && (
        <div data-testid="proof-confirmation-panel-heading">{heading}</div>
      )}
      {skills.map((sk) => (
        <div
          key={sk.skill}
          data-testid={`evidence-skill-${sk.skill}`}
          className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4"
        >
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-sm font-bold text-slate-900 dark:text-white">
              {sk.skill.replace(/_/g, " ")}
            </h3>
            {sk.strongest_state && <StateChip state={sk.strongest_state} />}
          </div>
          <ul className="mt-3 space-y-2">
            {sk.rows.map((row) => {
              const canPromote = (
                row.state === "ai_assisted" || row.state === "instructor_suggested"
              ) || row.state === "instructor_confirmed_partial";
              return (
                <li
                  key={row.id}
                  data-testid={`evidence-row-${row.id}`}
                  className="text-sm text-slate-700 dark:text-slate-300"
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-[150px]">
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        {row.provenance_line}
                      </p>
                      {row.notes && (
                        <p className="mt-1 text-xs italic text-slate-500 dark:text-slate-400">
                          {row.notes}
                        </p>
                      )}
                    </div>
                    <StateChip state={row.state} />
                  </div>
                  {canPromote && (
                    <div className="mt-2 flex items-center gap-2">
                      {row.state !== "instructor_confirmed_partial" && (
                        <button
                          type="button"
                          disabled={busyId === row.id}
                          onClick={() => promote(row.id, "instructor_confirmed_partial")}
                          data-testid={`confirm-partial-${row.id}`}
                          className="text-xs font-semibold text-sky-700 dark:text-sky-300 hover:underline disabled:opacity-50"
                        >
                          Confirm partially
                        </button>
                      )}
                      <button
                        type="button"
                        disabled={busyId === row.id}
                        onClick={() => promote(row.id, "instructor_confirmed")}
                        data-testid={`confirm-full-${row.id}`}
                        className="text-xs font-semibold text-emerald-700 dark:text-emerald-300 hover:underline disabled:opacity-50"
                      >
                        Confirm fully
                      </button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </section>
  );
}
